/**
 * One Advisor run (`/advisor/:runId`, under the Advisor entry): the inputs, a status line read out while the run works
 * (polled every 3 s), then the needs, matches, notices and near misses, the seller's feedback and outcome, and the
 * briefs: the internal seller brief, and the client brief after the seller previews and confirms it (the PDF prints
 * the brief as confirmed; when its content changed since, the page says so until the seller confirms again).
 */

import './advisor.css';

import { useState } from 'react';
import { FileDown, Loader2, Send, WandSparkles } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { isWorking, useAdvisorRun, useBriefPdf } from '@/api/queries';
import type { AdvisorRun } from '@/api/types';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { Badge } from '@/ui/Badge';
import { formatDateTime } from '@/ui/format';
import { PageHead } from '@/ui/PageHead';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';

import { briefChangedText, personName, statusLabel, statusLine, statusTone } from './advisorUi';
import { ClientBriefDialog } from './ClientBriefDialog';
import { FeedbackCard } from './FeedbackCard';
import { NewRunDialog } from './NewRunDialog';
import { RunResults } from './RunResults';

const back = { to: '/advisor', name: 'Asset Advisor' };

function StatusLine({ run }: { run: AdvisorRun }) {
  const line = statusLine(run);
  const working = isWorking(run.status);
  return (
    <div role="status" aria-live="polite" className={line ? `alert ${working ? 'alert-info' : 'alert-warning'}` : 'sr-only'}>
      {working && <Loader2 aria-hidden="true" className="spin" />}
      {line ?? `Run ${statusLabel(run.status).toLowerCase()}.`}
    </div>
  );
}

function Inputs({ run }: { run: AdvisorRun }) {
  return (
    <section className="card" aria-labelledby="advisor-inputs-title">
      <div className="card-body stack-md">
        <h2 id="advisor-inputs-title" className="advisor-title">The problem</h2>
        <p className="advisor-problem">{run.problem}</p>
        <p className="hint">
          {run.industry?.name ?? 'Cross-industry'} · {run.country ?? 'Country not stated'}
          {run.crm_reference && ` · CRM reference ${run.crm_reference}`}
        </p>
        {run.idea && (
          <p>Business opportunity: <Link to={`/business-opportunities/${run.idea.id}`}>{run.idea.title}</Link></p>
        )}
        {!run.idea && run.idea_linked && <p className="hint">Linked to a business opportunity you cannot open.</p>}
        {run.files.length > 0 && (
          <div className="field">
            <span className="label">Notes and transcripts</span>
            <ul className="advisor-list">
              {run.files.map((f) => (
                <li key={f.id}>
                  {f.filename}
                  {f.text_status === 'unreadable' && <span className="hint"> (no readable text, left out)</span>}
                  {f.text_truncated && <span className="hint"> (text cut at its limit)</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

function Actions({ run, onRevise, onClientBrief }: { run: AdvisorRun; onRevise: () => void; onClientBrief: () => void }) {
  const pdf = useBriefPdf(run.id);
  return (
    <>
      {run.can.seller_brief && (
        <ActionButton type="button" className="btn btn-secondary" state={buttonState(pdf)} icon={<FileDown aria-hidden="true" />}
          label="Seller brief" pendingLabel="Preparing" doneLabel="Opened" onClick={() => pdf.mutate('seller')} />
      )}
      {(run.client_brief || run.confirmed_client_brief) && (
        <button type="button" className="btn btn-secondary" onClick={onClientBrief}>
          <Send aria-hidden="true" /> Client brief
        </button>
      )}
      {run.can.rerun && !isWorking(run.status) && (
        <button type="button" className="btn btn-primary" onClick={onRevise}>
          <WandSparkles aria-hidden="true" /> Refine
        </button>
      )}
    </>
  );
}

function Subtitle({ run }: { run: AdvisorRun }) {
  return (
    <>
      <Badge tone={statusTone(run.status)}>{statusLabel(run.status)}</Badge>{' '}
      Started by {personName(run.created_by)} on {formatDateTime(run.created_at)}
    </>
  );
}

export function AdvisorRunPage() {
  const { runId = '' } = useParams();
  const query = useAdvisorRun(runId);
  const navigate = useNavigate();
  const [revising, setRevising] = useState(false);
  const [briefing, setBriefing] = useState(false);

  if (query.isPending) return <><PageHead title="Advisor run" back={back} /><LoadingCard label="Loading the run" /></>;
  if (query.error) return <><PageHead title="Advisor run" back={back} /><LoadFailure error={query.error} onRetry={() => void query.refetch()} /></>;
  const run = query.data;
  return (
    <>
      <PageHead title={run.client} back={back} subtitle={<Subtitle run={run} />}>
        <Actions run={run} onRevise={() => setRevising(true)} onClientBrief={() => setBriefing(true)} />
      </PageHead>
      <div className="stack-md advisor-run">
        <StatusLine run={run} />
        {run.client_brief_changed && (
          <div className="alert alert-warning" role="note">
            <span>{briefChangedText(run)}</span>{' '}
            <button type="button" className="btn btn-secondary" onClick={() => setBriefing(true)}>Review the client brief</button>
          </div>
        )}
        <Inputs run={run} />
        <RunResults run={run} />
        {run.status === 'succeeded' && <FeedbackCard key={run.id} run={run} />}
      </div>
      {revising && (
        <NewRunDialog source={run} onClose={() => setRevising(false)} onStarted={(next) => navigate(`/advisor/${next.id}`)} />
      )}
      {briefing && <ClientBriefDialog run={run} onClose={() => setBriefing(false)} />}
    </>
  );
}
